import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { Customer, CustomerStatus, Loan } from "@dfp/api-client";
import { Badge, type BadgeTone } from "../components/Badge.js";
import { Card, CardBody, CardHeader, CardTitle } from "../components/Card.js";
import { CreateCustomerForm } from "../components/CreateCustomerForm.js";
import { Table, TBody, THead, Th, Td, Tr } from "../components/Table.js";
import { api } from "../lib/api.js";

const STATUS_TONE: Record<CustomerStatus, BadgeTone> = {
  active: "success",
  suspended: "warning",
  deceased: "neutral",
  withdrawn: "danger",
};

export function CustomersPage(): JSX.Element {
  const customers = useQuery({
    queryKey: ["customers", "list"],
    queryFn: () => api.get<readonly Customer[]>("/api/customers"),
  });
  // Loan counts for the pivot column. Bounded client-side join (same as
  // LoansPage); the "Loans" cell deep-links into /loans?customerId=.
  const loans = useQuery({
    queryKey: ["loans", "list"],
    queryFn: () => api.get<readonly Loan[]>("/api/loans"),
  });
  const loanCountByCustomer = useMemo(() => {
    const counts = new Map<string, number>();
    for (const l of loans.data ?? []) {
      counts.set(l.customerId, (counts.get(l.customerId) ?? 0) + 1);
    }
    return counts;
  }, [loans.data]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Customers</h1>
        <p className="text-sm text-slate-500">Recent customers, all statuses.</p>
      </div>

      <CreateCustomerForm />

      <Card>
        <CardHeader>
          <CardTitle>All customers</CardTitle>
          <span className="text-xs text-slate-500">{customers.data?.length ?? 0} rows</span>
        </CardHeader>
        <CardBody className="p-0">
          {customers.isLoading ? (
            <p className="p-5 text-sm text-slate-500">Loading…</p>
          ) : customers.isError ? (
            <p className="p-5 text-sm text-red-700">Failed to load customers.</p>
          ) : (customers.data ?? []).length === 0 ? (
            <p className="p-5 text-sm text-slate-500">No customers yet.</p>
          ) : (
            <Table>
              <THead>
                <tr>
                  <Th>ID</Th>
                  <Th>Name</Th>
                  <Th>Status</Th>
                  <Th>Loans</Th>
                  <Th>Language</Th>
                  <Th>Risk</Th>
                  <Th>Vulnerable</Th>
                  <Th>Created</Th>
                </tr>
              </THead>
              <TBody>
                {customers.data?.map((c) => (
                  <Tr key={c.id}>
                    <Td>
                      <Link
                        to="/customers/$id"
                        params={{ id: c.id }}
                        className="font-mono text-xs text-brand-600 hover:underline"
                      >
                        {c.id.slice(0, 8)}…
                      </Link>
                    </Td>
                    <Td>
                      {c.givenName} {c.familyName}
                    </Td>
                    <Td>
                      <Badge tone={STATUS_TONE[c.status]}>{c.status}</Badge>
                    </Td>
                    <Td>
                      {(() => {
                        const count = loanCountByCustomer.get(c.id) ?? 0;
                        if (count === 0) {
                          return <span className="text-xs text-slate-400">—</span>;
                        }
                        return (
                          <Link
                            to="/loans"
                            search={{ customerId: c.id }}
                            className="text-sm text-brand-600 hover:underline"
                          >
                            {count} {count === 1 ? "loan" : "loans"}
                          </Link>
                        );
                      })()}
                    </Td>
                    <Td>{c.language}</Td>
                    <Td>{c.riskScore ?? "—"}</Td>
                    <Td>{c.vulnerableCustomer ? <Badge tone="warning">yes</Badge> : "no"}</Td>
                    <Td className="text-xs text-slate-500">
                      {new Date(c.createdAt).toLocaleDateString()}
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
